import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('taillink_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    return localStorage.getItem('taillink_token');
  });

  // Set up axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.defaults.timeout = 30000;
      axios.defaults.headers.common['Cache-Control'] = 'no-cache';
      axios.defaults.headers.common['Pragma'] = 'no-cache';

      const responseInterceptor = axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;

          if ((error.response?.status === 401 || error.response?.status === 405 || error.response?.status === 403) && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const refreshResponse = await axios.get(`${API}/auth/profile`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
                timeout: 15000
              });
              setUser(refreshResponse.data);
              localStorage.setItem('taillink_user', JSON.stringify(refreshResponse.data));
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              originalRequest.headers['Cache-Control'] = 'no-cache';
              return axios(originalRequest);
            } catch (refreshError) {
              alert('เซสชันหมดอายุ กรุณา refresh หน้าเว็บ (F5)');
              return Promise.reject(refreshError);
            }
          }

          if (!originalRequest._networkRetry && (error.code === 'ECONNABORTED' || error.message === 'Network Error' || error.response?.status >= 500)) {
            originalRequest._networkRetry = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            return axios(originalRequest);
          }

          return Promise.reject(error);
        }
      );

      return () => { axios.interceptors.response.eject(responseInterceptor); };
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check URL for auth token (from Google OAuth callback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');
    const authError = urlParams.get('auth_error');

    if (authToken) {
      setToken(authToken);
      localStorage.setItem('taillink_token', authToken);
      const getUserData = async () => {
        try {
          const response = await axios.get(`${API}/auth/profile`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          localStorage.setItem('taillink_user', JSON.stringify(response.data));
          setUser(response.data);
        } catch (error) {
          console.error('Failed to get user data after OAuth:', error);
        }
      };
      getUserData();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (authError) {
      const detail = urlParams.get('auth_error_detail') || '';
      console.error('Authentication error:', authError, detail);
      const msg = detail
        ? `Login failed: ${detail}`
        : 'Login failed. Please try again.';
      alert(msg);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Load user data when token changes
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        if (!user) {
          try {
            const response = await axios.get(`${API}/auth/profile`);
            const userData = response.data;
            setUser(userData);
            localStorage.setItem('taillink_user', JSON.stringify(userData));
          } catch (error) {
            console.error('Failed to load user:', error);
          }
        }
      } else {
        setUser(null);
        localStorage.removeItem('taillink_user');
      }
      setLoading(false);
    };
    loadUser();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      saveUserSession(access_token, userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, username, password, fullName = '') => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email, username, password, full_name: fullName
      });
      if (!response.data || !response.data.access_token || !response.data.user) {
        return { success: false, error: 'Invalid response from server' };
      }
      const { access_token, user: userData } = response.data;
      saveUserSession(access_token, userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, timeout: 5000 }); } catch {}
    localStorage.removeItem('taillink_token');
    localStorage.removeItem('taillink_user');
    localStorage.removeItem('tl_swr_cache_v1');
    localStorage.removeItem('tl_post_login_redirect');
    document.cookie = 'access_token=; Max-Age=0; path=/; SameSite=Lax';
    document.cookie = 'refresh_token=; Max-Age=0; path=/; SameSite=Lax';
    document.cookie = 'taillink_token=; Max-Age=0; path=/; SameSite=Lax';
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    window.dispatchEvent(new Event('tl-auth-changed'));
    window.location.reload();
  };

  const saveUserSession = (tokenData, userData) => {
    localStorage.setItem('taillink_token', tokenData);
    localStorage.setItem('taillink_user', JSON.stringify(userData));
    setToken(tokenData);
    setUser(userData);
  };

  const refreshSession = async () => {
    if (!token) return { success: false, error: 'No token available' };
    try {
      axios.defaults.timeout = 15000;
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        timeout: 15000
      });
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('taillink_user', JSON.stringify(userData));
      axios.defaults.timeout = 30000;
      return { success: true };
    } catch (error) {
      axios.defaults.timeout = 30000;
      let errorMessage = 'Session refresh failed';
      if (error.response?.status === 405) errorMessage = 'เซิร์ฟเวอร์มีปัญหา กรุณา refresh หน้าเว็บ (F5)';
      else if (error.response?.status === 401) errorMessage = 'Token หมดอายุ กรุณา login ใหม่';
      else if (error.code === 'ECONNABORTED') errorMessage = 'Connection timeout กรุณาลองใหม่';
      else if (error.message === 'Network Error') errorMessage = 'Network error กรุณาตรวจสอบการเชื่อมต่อ';
      return { success: false, error: errorMessage };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API}/auth/profile`, profileData);
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('taillink_user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Profile update failed' };
    }
  };

  const loginWithGoogle = () => {
    window.location.href = `${API}/auth/google`;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    refreshSession,
    loginWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
